import { Editor, Element, Path, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

/**
 * Extracts column information (index and table path) from a table header element
 * @param editor - Slate editor instance
 * @param element - The table header element
 * @returns Object containing columnIndex and tablePath, or null values if error occurs
 */
export const getColumnInfo = (editor, element) => {
    try {
        const elementPath = (ReactEditor as any).findPath(editor, element);
        const columnIndex = elementPath[elementPath.length - 1];
        const tablePath = elementPath.slice(0, -2);
        return { columnIndex, tablePath };
    } catch (error) {
        console.error('Error getting column info:', error);
        return { columnIndex: null, tablePath: null };
    }
};

/**
 * Checks if the current column is selected by comparing with editor's selectedColumns array
 * @param editor - Slate editor instance
 * @param element - The table header element to check
 * @returns Boolean indicating if the column is currently selected
 */
export const isCurrentColumnSelected = (editor, element) => {
    const { columnIndex, tablePath } = getColumnInfo(editor, element);
    if (columnIndex === null || !tablePath) return false;

    return editor.selectedColumns?.some((col) => col.columnIndex === columnIndex && Path.equals(col.tablePath, tablePath)) || false;
};

/**
 * Handles column selection/deselection when a column header is clicked
 * Supports multiple column selection - clicking adds/removes from selection
 * Clears any existing row selections when selecting columns
 * @param e - Click event
 * @param editor - Slate editor instance
 * @param element - The table header element that was clicked
 */
export const handleColumnSelect = (e, editor, element) => {
    e.preventDefault();
    e.stopPropagation();

    const { columnIndex, tablePath } = getColumnInfo(editor, element);
    if (columnIndex === null || !tablePath) return;

    // Clear any existing row selections first (columns and rows are mutually exclusive)
    if (editor.selectedRows?.length > 0) {
        editor.selectedRows.forEach(({ tablePath: selectedTablePath, rowIndex: selectedRowIndex }) => {
            applyRowSelection(editor, selectedTablePath, selectedRowIndex, false);
        });
        editor.selectedRows = [];
        editor.selectedRow = null;
    }

    // Initialize selectedColumns array if it doesn't exist
    if (!editor.selectedColumns) {
        editor.selectedColumns = [];
    }

    const existingIndex = editor.selectedColumns.findIndex(
        (col) => col.columnIndex === columnIndex && Path.equals(col.tablePath, tablePath),
    );

    // Toggle column selection: if already selected, deselect it; otherwise add to selection
    if (existingIndex !== -1) {
        editor.selectedColumns = editor.selectedColumns.filter((_, index) => index !== existingIndex);
        applyColumnSelection(editor, tablePath, columnIndex, false);
    } else {
        editor.selectedColumns = [
            ...editor.selectedColumns,
            {
                columnIndex,
                tablePath: [...tablePath], // Clone path to avoid reference issues
                isSelected: true,
            },
        ];
        applyColumnSelection(editor, tablePath, columnIndex, true);
    }

    // Update legacy selectedColumn for backward compatibility
    if (editor.selectedColumns.length > 0) {
        editor.selectedColumn = editor.selectedColumns[editor.selectedColumns.length - 1];
    } else {
        editor.selectedColumn = null;
    }

    editor.onChange();
};

/**
 * Extracts row information (index and table path) from a table cell element
 * @param editor - Slate editor instance
 * @param element - The table cell element
 * @returns Object containing rowIndex and tablePath, or null values if error occurs
 */
const getRowInfo = (editor, element) => {
    try {
        const elementPath = (ReactEditor as any).findPath(editor, element);
        const rowIndex = elementPath[elementPath.length - 2];
        const tablePath = elementPath.slice(0, -2);
        return { rowIndex, tablePath };
    } catch (error) {
        console.error('Error getting row info:', error);
        return { rowIndex: null, tablePath: null };
    }
};

/**
 * Handles row selection/deselection when a row selector is clicked
 * Supports multiple row selection - clicking adds/removes from selection
 * Clears any existing column selections when selecting rows
 * @param e - Click event
 * @param editor - Slate editor instance
 * @param element - The table cell element that was clicked
 */
export const handleRowSelect = (e, editor, element) => {
    e.preventDefault();
    e.stopPropagation();

    const { rowIndex, tablePath } = getRowInfo(editor, element);
    if (!tablePath || rowIndex === null) return;

    // Clear any existing column selections first (rows and columns are mutually exclusive)
    if (editor.selectedColumns?.length > 0) {
        editor.selectedColumns.forEach(({ tablePath: selectedTablePath, columnIndex: selectedColumnIndex }) => {
            applyColumnSelection(editor, selectedTablePath, selectedColumnIndex, false);
        });
        editor.selectedColumns = [];
        editor.selectedColumn = null;
    }

    // Initialize selectedRows array if it doesn't exist
    if (!editor.selectedRows) {
        editor.selectedRows = [];
    }

    const existingIndex = editor.selectedRows.findIndex((row) => row.rowIndex === rowIndex && Path.equals(row.tablePath, tablePath));

    // Toggle row selection: if already selected, deselect it; otherwise add to selection
    if (existingIndex !== -1) {
        editor.selectedRows = editor.selectedRows.filter((_, index) => index !== existingIndex);
        applyRowSelection(editor, tablePath, rowIndex, false);
    } else {
        editor.selectedRows = [
            ...editor.selectedRows,
            {
                rowIndex,
                tablePath: [...tablePath], // Clone path to avoid reference issues
                isSelected: true,
            },
        ];
        applyRowSelection(editor, tablePath, rowIndex, true);
    }

    // Update legacy selectedRow for backward compatibility
    if (editor.selectedRows.length > 0) {
        editor.selectedRow = editor.selectedRows[editor.selectedRows.length - 1];
    } else {
        editor.selectedRow = null;
    }

    editor.onChange();
};

/**
 * Applies visual selection styling to all cells in a specific column
 * Sets or removes the 'isColumnSelected' property on each cell in the column
 * @param editor - Slate editor instance
 * @param tablePath - Path to the table in the document
 * @param columnIndex - Index of the column to apply selection to
 * @param isSelected - Boolean indicating whether to select (true) or deselect (false)
 */
export const applyColumnSelection = (editor, tablePath, columnIndex, isSelected) => {
    try {
        const [tableNode] = Editor.node(editor, tablePath);

        if (!Element.isElement(tableNode)) return;

        Editor.withoutNormalizing(editor, () => {
            // Iterate through all rows and update the cell at columnIndex
            tableNode.children.forEach((row, rowIndex) => {
                if (Element.isElement(row) && columnIndex < row.children.length) {
                    const cellPath = [...tablePath, rowIndex, columnIndex];
                    Transforms.setNodes(editor, { isColumnSelected: isSelected } as any, { at: cellPath });
                }
            });
        });
    } catch (error) {
        console.error('Error applying column selection:', error);
    }
};

/**
 * Applies visual selection styling to all cells in a specific row
 * Sets or removes the 'isRowSelected' property on each cell in the row
 * @param editor - Slate editor instance
 * @param tablePath - Path to the table in the document
 * @param rowIndex - Index of the row to apply selection to
 * @param isSelected - Boolean indicating whether to select (true) or deselect (false)
 */
export const applyRowSelection = (editor, tablePath, rowIndex, isSelected) => {
    try {
        const [tableNode] = Editor.node(editor, tablePath);

        if (!Element.isElement(tableNode)) return;

        Editor.withoutNormalizing(editor, () => {
            // Get the specific row and update all its cells
            const row = tableNode.children[rowIndex];
            if (Element.isElement(row)) {
                row.children.forEach((cell, cellIndex) => {
                    const cellPath = [...tablePath, rowIndex, cellIndex];
                    Transforms.setNodes(editor, { isRowSelected: isSelected } as any, { at: cellPath });
                });
            }
        });
    } catch (error) {
        console.error('Error applying row selection:', error);
    }
};

/**
 * Resets all selected rows and columns in the editor
 * Removes visual selection styling and clears selection arrays
 * @param editor - Slate editor instance
 */
export const resetAllSelections = (editor) => {
    // Reset selected columns
    if (editor.selectedColumns?.length > 0) {
        editor.selectedColumns.forEach(({ tablePath: selectedTablePath, columnIndex: selectedColumnIndex }) => {
            applyColumnSelection(editor, selectedTablePath, selectedColumnIndex, false);
        });
        editor.selectedColumns = [];
        // editor.selectedColumn = null;
    }

    // Reset selected rows
    if (editor.selectedRows?.length > 0) {
        editor.selectedRows.forEach(({ tablePath: selectedTablePath, rowIndex: selectedRowIndex }) => {
            applyRowSelection(editor, selectedTablePath, selectedRowIndex, false);
        });
        editor.selectedRows = [];
        // editor.selectedRow = null;
    }

    editor.onChange();
};
